import { Link } from "../components/LocaleLink";
import { motion } from "framer-motion";
import { Check, ArrowRight, MapPin, Building2, TrendingUp, Hand } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { PressStrip } from "../components/PressStrip";

const IMG_ABOUT = "https://images.unsplash.com/photo-1763506754901-fb5be9c1add7";
const IMG_CARE = "https://images.pexels.com/photos/8082227/pexels-photo-8082227.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const WHY_ICONS = [MapPin, Building2, TrendingUp, Hand];
const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function About() {
  const { t } = useLang();
  const a = (k) => t(`aboutPage.${k}`);
  const whyContrast = a("whyContrast");
  const whyPoints = a("whyPoints");
  const carePoints = a("carePoints");

  return (
    <div data-testid="about-page">
      <Seo title={`${t("nav.about")} | Velmira Living`} description={a("intro")} />
      {/* HERO / INTRO */}
      <section className="pt-36 md:pt-44 pb-20 md:pb-28">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <p className={overline}>{a("overline")}</p>
              <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl">{a("title")}</h1>
              <p className="mt-8 font-body font-light text-lg text-foreground/75 leading-relaxed max-w-2xl">{a("intro")}</p>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.1}>
              <div className="aspect-[4/5] overflow-hidden">
                <img src={optimizeImg(IMG_ABOUT, 900)} alt="Côte d'Azur architecture" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY VELMIRA */}
      <section id="why-velmira" data-testid="why-velmira-section" className="scroll-mt-24 bg-secondary border-y border-border/60 py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-20">
          <div className="md:col-span-5">
            <Reveal>
              <p className={overline}>{a("whyOverline")}</p>
              <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{a("whyTitle")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 font-body font-light text-lg text-foreground/75">{a("whyIntro")}</p>
              <ul className="mt-4 flex flex-col gap-3">
                {whyContrast.map((c, i) => (
                  <li key={i} className="font-headings text-2xl text-foreground/60 italic">{c}</li>
                ))}
              </ul>
              <p className="mt-6 font-headings text-2xl text-primary leading-snug">{a("whyMiddle")}</p>
            </Reveal>
          </div>

          <div className="md:col-span-7 md:pt-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border/50">
              {whyPoints.map((p, i) => {
                const Icon = WHY_ICONS[i] || Check;
                return (
                  <Reveal key={i} delay={(i % 2) * 0.1} className="bg-secondary">
                    <div className="p-8 h-full hover:bg-card transition-colors duration-500">
                      <Icon size={24} className="text-primary" strokeWidth={1.4} />
                      <p className="mt-4 font-body text-lg text-foreground/90 leading-snug">{p}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.2}>
              <p className="mt-10 font-headings text-3xl md:text-4xl text-foreground leading-tight">{a("whyClosing")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* GET TO KNOW US */}
      <section data-testid="get-to-know-section" className="py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-start">
          <div className="md:col-span-5">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden md:sticky md:top-28">
                <img src={optimizeImg(IMG_CARE, 900)} alt="Managed luxury interior" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <Reveal>
              <p className={overline}>{a("careOverline")}</p>
              <h2 className="mt-5 font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{a("careTitle")}</h2>
              <p className="mt-6 font-body text-xl text-foreground/90 font-light">{a("careP1")}</p>
              <p className="mt-4 font-body font-light text-lg text-foreground/70 leading-relaxed">{a("careP2")}</p>
            </Reveal>

            <ul className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {carePoints.map((p, i) => (
                <Reveal key={i} delay={(i % 2) * 0.06}>
                  <li className="flex items-center gap-3 border-b border-border/50 pb-4 font-body font-light text-foreground/85">
                    <Check size={18} className="text-primary shrink-0" /> {p}
                  </li>
                </Reveal>
              ))}
            </ul>

            <Reveal delay={0.15}>
              <p className="mt-10 font-headings text-2xl md:text-3xl text-primary">{a("careClosing")}</p>
              <Link to="/contact?interest=valuation" data-testid="about-cta-consultation" className="mt-8 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
                {a("ctaButton")} <ArrowRight size={15} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BRAND STORY — Vel + Mira philosophy */}
      <section
        data-testid="about-brand-story"
        aria-labelledby="about-brand-story-title"
        className="bg-secondary border-t border-border/60 py-20 md:py-28"
      >
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className={`${overline} justify-center`}>{a("brandStoryOverline")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              id="about-brand-story-title"
              className="mt-8 font-headings text-2xl md:text-3xl lg:text-4xl leading-[1.35] tracking-tight text-foreground/90"
            >
              {a("brandStory")}
            </p>
          </Reveal>
        </div>
      </section>

      <PressStrip />
    </div>
  );
}
